import React, { Fragment, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { H5, Breadcrumbs } from "../../AbstractElements";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Button,
} from "reactstrap";
import FooterCard from "../../Components/Forms/FormControl/Common/FooterCard";
import { titleCase } from "../../utils/case";
import { createReservation, getReservationMasterData } from "../../api/reservation.api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const ReservationCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [lots, setLots] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [costOverrides, setCostOverrides] = useState({});

  const [form, setForm] = useState({
    name: "",
    phoneNo: "",
    email: "",
    guestQty: 1,
    guests: [
      {
        name: "",
        nricPassportNo: "",
        phoneNo: "",
        carPlateNo: "",
        carBrand: "",
        carModel: "",
        carColor: "",
      },
    ],
    startDate: "",
    endDate: "",
    status: "pending",
    type: "booking",
    remark: "",
    packageId: "",
    lots: [],
    addOns: [],
  });

  const addGuest = () =>
    setForm(prev => ({
      ...prev,
      guestQty: prev.guestQty + 1,
      guests: [...prev.guests, {
        name: "",
        nricPassportNo: "",
        phoneNo: "",
        carPlateNo: "",
        carBrand: "",
        carModel: "",
        carColor: "",
      }],
    }));

  const removeGuest = index =>
    setForm(prev => {
      const guests = prev.guests.filter((_, i) => i !== index);
      return { ...prev, guestQty: guests.length, guests };
    });

  const updateGuest = (index, field, value) =>
    setForm(prev => ({
      ...prev,
      guests: prev.guests.map((guest, i) =>
        i === index ? { ...guest, [field]: value } : guest
      ),
    }));

  const isBooking = form.type === "booking";
  
  const isNonBooking = form.type !== "booking";

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getReservationMasterData();
        setLots(data.lots || []);
        setAddOns(data.addOns || []);
        setPackages(data.packages || []);
      } catch {
        toast.error("Failed to load master data");
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!isBooking) {
      setAvailableAddOns(addOns.filter((a) => a.status === "active"));
      return;
    }

    const allowed = new Map();

    form.lots.forEach((lotId) => {
      const lot = lots.find((l) => l.id === lotId);

      lot?.lotAddOns?.forEach(({ status, addOn }) => {
        if (status === "active" && addOn?.status === "active") {
          allowed.set(addOn.id, addOn);
        }
      });
    });

    setForm((prev) => ({
      ...prev,
      addOns: prev.addOns.filter((a) => allowed.has(a.id)),
    }));

    setAvailableAddOns([...allowed.values()]);
  }, [form.type, form.lots, lots, addOns]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleLot = (lotId) => {
    setForm((prev) => {
      const exists = prev.lots.includes(lotId);

      return {
        ...prev,
        lots: exists ? prev.lots.filter((id) => id !== lotId) : [...prev.lots, lotId],
      };
    });
  };

  const toggleAddOn = (addOnId) => {
    setForm((prev) => ({
      ...prev,
      addOns: prev.addOns.some((addOn) => addOn.id === addOnId)
        ? prev.addOns.filter((addOn) => addOn.id !== addOnId)
        : [...prev.addOns, { id: addOnId, quantity: 1 }],
    }));
  };

  const updateAddOnQty = (addOnId, qty) => {
    setForm((prev) => ({
      ...prev,
      addOns: prev.addOns.map((addOn) =>
        addOn.id === addOnId ? { ...addOn, quantity: Number(qty) } : addOn
      ),
    }));
  };

  const configuredPrice = (item) => {
    let pricing = item?.pricing;
    if (typeof pricing === "string") {
      try { pricing = JSON.parse(pricing); } catch { return 0; }
    }
    const prices = Array.isArray(pricing) ? pricing : pricing ? [pricing] : [];
    const preferred = prices.find(p => p.chargedBy === "flat_fee" && (!p.season || p.season === "non_peak"))
      ?? prices.find(p => !p.season || p.season === "non_peak")
      ?? prices[0];
    return Number(preferred?.amount ?? 0);
  };

  const lineItems = [
    ...(form.packageId ? [{ key: `package:${form.packageId}`, label: selectedPackage?.name || packages.find(p => Number(p.id) === Number(form.packageId))?.name || "Package", quantity: 1, unitCost: configuredPrice(selectedPackage || packages.find(p => Number(p.id) === Number(form.packageId))) }] : []),
    ...form.lots.map(id => { const lot = lots.find(l => Number(l.id) === Number(id)); return { key: `lot:${id}`, label: lot?.name || `Lot #${id}`, quantity: 1, unitCost: configuredPrice(lot) }; }),
    ...form.addOns.map(a => { const addOn = addOns.find(x => Number(x.id) === Number(a.id)); return { key: `add_on:${a.id}`, label: addOn?.name || `Add-on #${a.id}`, quantity: Number(a.quantity), unitCost: configuredPrice(addOn) }; }),
  ];
  const itemCost = (item) => Object.prototype.hasOwnProperty.call(costOverrides, item.key)
    ? Number(costOverrides[item.key]) : Number((item.unitCost * item.quantity).toFixed(2));
  const totalCost = lineItems.reduce((sum, item) => sum + itemCost(item), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!form.lots.length) return toast.error("Please select at least one lot.");
      if (!form.startDate || !form.endDate) {
        return toast.error("Start and end dates are required.");
      }

      const payload = {
        ...form,
        packageId: form.packageId || null,
        itemCosts: Object.fromEntries(lineItems.map(item => [item.key, itemCost(item)])),
        lots: form.lots.map(Number),
        addOns: form.addOns.map(a => ({
          id: Number(a.id),
          quantity: Number(a.quantity),
        })),
        guestQty: form.guests.length,
        guests: form.guests,
      };

      if (!isBooking) {
        Object.assign(payload, {
          name: "-",
          phoneNo: "-",
          email: "-",
          status: "confirmed",
        });
      }

      setLoading(true);
      await createReservation(payload);
      toast.success("Reservation created successfully");
      navigate("/reservations");
    } catch (err) {
      toast.error(err.message || "Failed to create reservation");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const triggerSubmit = () => {
    if (new Date(form.endDate) < new Date(form.startDate)) {
      return toast.error(
        "End date cannot be before start date."
      );
    }
    formRef.current?.requestSubmit();
  };

  const selectPackage = (packageId) => {
    if (!packageId) {
      setSelectedPackage(null);
      setForm((prev) => ({ ...prev, packageId: "", lots: [], addOns: [] }));
      return;
    }

    const pkg = packages.find((p) => p.id === Number(packageId));
    if (!pkg) return;

    setSelectedPackage(pkg);

    setForm((prev) => ({
      ...prev,
      packageId,
      lots: pkg.items
        .filter((i) => i.itemType === "lot")
        .map((i) => i.itemId),
      addOns: pkg.items
        .filter((i) => i.itemType === "add_on")
        .map((i) => ({ id: i.itemId, quantity: i.quantity })),
    }));
  };

  const packageLots = selectedPackage?.items.filter((i) => i.itemType === "lot") ?? [];
  const packageAddOns = selectedPackage?.items.filter((i) => i.itemType === "add_on") ?? [];

  const displayedLots = form.packageId ? packageLots : lots;

  const additionalAddOns = availableAddOns.filter(
    (addOn) => !packageAddOns.some((item) => item.itemId === addOn.id)
  );

  const displayedAddOns = availableAddOns.map((addOn) => {
    const included = form.packageId
      ? packageAddOns.find((p) => p.itemId === addOn.id)
      : null;

    return {
      ...addOn,
      includedQty: included?.quantity || 0,
    };
  });

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservation"))}
        title={titleCase(t("new reservation"))}
        mainTitle={titleCase(t("new reservation"))}
      />

      <Container fluid>
        <Row>
          <Col>
            <Card className="mb-4">
              <CardBody>
                <Form innerRef={formRef} onSubmit={handleSubmit}>

                  {/* TYPE (BUTTON SELECTION) */}
                  <FormGroup>
                    <Label>{titleCase(t("reservation type"))}</Label>
                    <div className="d-flex gap-2">
                      <Button
                        type="button"
                        color={form.type === "booking" ? "primary" : "light"}
                        onClick={() => handleChange("type", "booking")}
                      >
                        {titleCase(t("booking"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "special_event" ? "primary" : "light"}
                        onClick={() =>
                          handleChange("type", "special_event")
                        }
                      >
                        {titleCase(t("special event"))}
                      </Button>

                      <Button
                        type="button"
                        color={form.type === "maintenance" ? "primary" : "light"}
                        onClick={() => handleChange("type", "maintenance")}
                      >
                        {titleCase(t("maintenance"))}
                      </Button>
                    </div>
                  </FormGroup>

                  {/* BOOKING ONLY FIELDS */}
                  {!isNonBooking && (
                    <>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("name"))}</Label>
                            <Input
                              type="text"
                              value={form.name}
                              onChange={(e) =>
                                handleChange("name", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("phone number"))}</Label>
                            <Input
                              type="text"
                              value={form.phoneNo}
                              onChange={(e) =>
                                handleChange("phoneNo", e.target.value)
                              }
                              required
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <FormGroup>
                        <Label>{titleCase(t("email"))}</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) =>
                            handleChange("email", e.target.value)
                          }
                          required
                        />
                      </FormGroup>

                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>{titleCase(t("status"))}</Label>
                            <Input
                              type="select"
                              value={form.status}
                              onChange={(e) =>
                                handleChange("status", e.target.value)
                              }
                            >
                              <option value="pending">
                                {titleCase(t("pending"))}
                              </option>
                              <option value="unpaid">
                                {titleCase(t("unpaid"))}
                              </option>
                              <option value="confirmed">
                                {titleCase(t("confirmed"))}
                              </option>
                              <option value="cancelled">
                                {titleCase(t("cancelled"))}
                              </option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </>
                  )}

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>{titleCase(t("start date"))}</Label>
                        <Input
                          type="date"
                          value={form.startDate}
                          onChange={(e) => handleChange("startDate", e.target.value)}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="6">
                      <FormGroup>
                        <Label>{titleCase(t("end date"))}</Label>
                        <Input
                          type="date"
                          value={form.endDate}
                          onChange={(e) => handleChange("endDate", e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label>{titleCase(t("remark"))}</Label>
                    <Input
                      type="textarea"
                      rows={4}
                      value={form.remark}
                      onChange={(e) => handleChange("remark", e.target.value)}
                    />
                  </FormGroup>
                </Form>
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <H5 className="mb-4">{titleCase(t("reserved items"))}</H5>


                {isBooking && (
                  <>
                    <FormGroup>
                      <Label>{titleCase(t("package"))}</Label>

                      <div className="m-checkbox-inline custom-radio-ml d-flex flex-wrap">
                        <div className="radio radio-primary me-4 mb-2">
                          <Input
                            id="package-none"
                            type="radio"
                            name="package"
                            checked={!form.packageId}
                            onChange={() => selectPackage("")}
                          />
                          <Label className="mb-0" for="package-none">
                            No Package
                          </Label>
                        </div>

                        {packages.map((pkg) => (
                          <div key={pkg.id} className="radio radio-primary me-4 mb-2">
                            <Input
                              id={`package-${pkg.id}`}
                              type="radio"
                              name="package"
                              checked={Number(form.packageId) === pkg.id}
                              onChange={() => selectPackage(pkg.id)}
                            />
                            <Label className="mb-0" for={`package-${pkg.id}`}>
                              {pkg.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </FormGroup>

                    {/* Lots */}
                    <FormGroup>
                      <Label className="fw-bold">{titleCase(t("camp lots"))}</Label>

                      <Row>
                        {
                          displayedLots.map((item) => {
                            const lot = form.packageId ? item.lot : item;

                            return (
                              <Col md="4" lg="3" key={lot.id} className="mb-2">
                                <div className="checkbox checkbox-primary">
                                  <Input
                                    id={`lot-${lot.id}`}
                                    type="checkbox"
                                    checked={form.lots.includes(lot.id)}
                                    disabled={
                                      !!form.packageId &&
                                      !form.lots.includes(lot.id) &&
                                      form.lots.length >= selectedPackage.maxLotAllowed
                                    }
                                    onChange={() => toggleLot(lot.id)}
                                  />
                                  <Label for={`lot-${lot.id}`}>{lot.name}</Label>
                                </div>
                              </Col>
                            );
                        })}
                      </Row>
                    </FormGroup>

                    <hr />

                    {/* Add Ons */}
                    <FormGroup>
                      <Row className="fw-bold mb-2">
                        <Col xs="12" lg="6"><Row><Col xs="6">Add-on</Col><Col xs="3">Included</Col><Col xs="3">Additional</Col></Row></Col>
                        <Col xs="12" lg="6" className="d-none d-lg-block"><Row><Col xs="6">Add-on</Col><Col xs="3">Included</Col><Col xs="3">Additional</Col></Row></Col>
                      </Row>
                      <Row>
                        {displayedAddOns.map((addOn) => {
                          const selected = form.addOns.find((a) => a.id === addOn.id);

                          return (
                            <Col xs="12" lg="6" key={addOn.id} className="mb-3">
                              <Row className="align-items-center">
                                <Col xs="6">
                                  <div className="checkbox checkbox-primary">
                                    <Input
                                      id={`addon-${addOn.id}`}
                                      type="checkbox"
                                      checked={!!selected}
                                      onChange={() => toggleAddOn(addOn.id)}
                                    />
                                    <Label for={`addon-${addOn.id}`}>{addOn.name}</Label>
                                  </div>
                                </Col>

                                <Col xs="6">
                                  {selected && (
                                    <Row>
                                      <Col xs="6">
                                        <Input type="number" disabled value={addOn.includedQty} />
                                      </Col>

                                      <Col xs="6">
                                        <Input
                                          type="number"
                                          min={0}
                                          value={Math.max(0, selected.quantity - addOn.includedQty)}
                                          onChange={(e) =>
                                            updateAddOnQty(
                                              addOn.id,
                                              Number(e.target.value) + addOn.includedQty
                                            )
                                          }
                                        />
                                      </Col>
                                    </Row>
                                  )}
                                </Col>
                              </Row>
                            </Col>
                          );
                        })}
                      </Row>
                    </FormGroup>
                  </>
                )}

                {!isBooking && (
                  <>
                    <FormGroup>
                      <Label className="fw-bold">{titleCase(t("camp lots"))}</Label>

                      <Row>
                        {lots.map((lot) => (
                          <Col md="4" lg="3" key={lot.id} className="mb-2">
                            <div className="checkbox checkbox-primary">
                              <Input
                                id={`lot-${lot.id}`}
                                type="checkbox"
                                checked={form.lots.includes(lot.id)}
                                onChange={() => toggleLot(lot.id)}
                              />
                              <Label for={`lot-${lot.id}`}>{lot.name}</Label>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </FormGroup>

                    <hr />

                    {/* Add Ons */}
                    <FormGroup>
                      <Label className="fw-bold">{titleCase(t("add-ons"))}</Label>

                      <Row>
                        {availableAddOns.map((addOn) => {
                          const selected = form.addOns.find((a) => a.id === addOn.id);

                          return (
                            <Col md="6" key={addOn.id} className="mb-3">
                              <Row className="align-items-center">
                                <Col xs="8">
                                  <div className="checkbox checkbox-primary">
                                    <Input
                                      id={`addon-${addOn.id}`}
                                      type="checkbox"
                                      checked={!!selected}
                                      onChange={() => toggleAddOn(addOn.id)}
                                    />
                                    <Label for={`addon-${addOn.id}`}>{addOn.name}</Label>
                                  </div>
                                </Col>

                                <Col xs="4">
                                  {selected && (
                                    <Input
                                      type="number"
                                      min={1}
                                      value={selected.quantity}
                                      onChange={(e) =>
                                        updateAddOnQty(addOn.id, e.target.value)
                                      }
                                    />
                                  )}
                                </Col>
                              </Row>
                            </Col>
                          );
                        })}
                      </Row>
                    </FormGroup>
                  </>
                )}
              </CardBody>
            </Card>

            <Card className="mb-4">
              <CardBody>
                <H5 className="mb-4">Cost Calculation</H5>
                <div className="table-responsive mt-4">
                  <table className="table table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Reserved Item</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Original Unit Cost (RM)</th>
                        <th className="text-end">Original Cost (RM)</th>
                        <th style={{ minWidth: 160 }}>Final Cost (RM)</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map(item => (
                        <tr key={item.key}>
                          <td>{item.label}</td>
                          <td className="text-end">{item.quantity}</td>
                          <td className="text-end">{item.unitCost.toFixed(2)}</td>
                          <td className="text-end">{(item.unitCost * item.quantity).toFixed(2)}</td>
                          <td>
                            <Input type="number" min="0" step="0.01"
                              aria-label={`Final cost for ${item.label}`}
                              value={itemCost(item)}
                              onChange={e => setCostOverrides(prev => ({ ...prev, [item.key]: e.target.value }))}
                            />
                          </td>
                          <td>
                            <Button type="button" color="light" size="sm"
                              onClick={() => setCostOverrides(prev => {
                                const next = { ...prev };
                                delete next[item.key];
                                return next;
                              })}>
                              Reset to default
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!lineItems.length && <tr><td colSpan="6" className="text-center text-muted">No items selected</td></tr>}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th colSpan="4" className="text-end">Total</th>
                        <th colSpan="2">RM {totalCost.toFixed(2)}</th>
                      </tr>
                    </tfoot>
                  </table>
                </div>

              </CardBody>
            </Card>



            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <H5>Guests</H5>

                  <Button
                    color="primary"
                    size="sm"
                    type="button"
                    onClick={addGuest}
                  >
                    + Add Guest
                  </Button>
                </div>

                {form.guests.map((guest, index) => (
                  <Card key={index} className="mb-3 border">
                    <CardBody>
                      <div className="d-flex justify-content-between mb-3">
                        <strong>Guest #{index + 1}</strong>

                        {form.guests.length > 1 && (
                          <Button
                            color="danger"
                            size="sm"
                            type="button"
                            onClick={() => removeGuest(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>Name</Label>
                            <Input
                              value={guest.name}
                              onChange={(e) =>
                                updateGuest(index, "name", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>IC / Passport</Label>
                            <Input
                              value={guest.nricPassportNo}
                              onChange={(e) =>
                                updateGuest(index, "nricPassportNo", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>Phone</Label>
                            <Input
                              value={guest.phoneNo}
                              onChange={(e) =>
                                updateGuest(index, "phoneNo", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="6">
                          <FormGroup>
                            <Label>Vehicle Plate</Label>
                            <Input
                              value={guest.carPlateNo}
                              onChange={(e) =>
                                updateGuest(index, "carPlateNo", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label>Brand</Label>
                            <Input
                              value={guest.carBrand}
                              onChange={(e) =>
                                updateGuest(index, "carBrand", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label>Model</Label>
                            <Input
                              value={guest.carModel}
                              onChange={(e) =>
                                updateGuest(index, "carModel", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>

                        <Col md="4">
                          <FormGroup>
                            <Label>Color</Label>
                            <Input
                              value={guest.carColor}
                              onChange={(e) =>
                                updateGuest(index, "carColor", e.target.value)
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                ))}
              </CardBody>
            </Card>

            <FooterCard
              onSubmit={triggerSubmit}
              onCancel={handleCancel}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationCreate;